/**
 * Session contracts — the key ceremony, the proposal, and intake.
 *
 * # Why this screen is full of file downloads and uploads rather than buttons
 *
 * The client's signing key never touches Conduit. It is generated on the client's own machine by
 * `conduit-sign`, which has no HTTP client, no async runtime, and no socket of any kind — so there
 * is no version of this screen that "just signs it for you".
 *
 * What this screen can do is make the manual exchange hard to get wrong: export the proposal as a
 * file, say plainly what the client is being asked to agree to, and take the signed result back.
 *
 * # The fingerprint field is the ceremony
 *
 * Pinning a key without confirming its fingerprint through a second channel is pasting 64
 * characters somebody emailed you. If that email was intercepted, the root of trust for the
 * client's entire estate has been replaced at a step where nobody would have noticed. So the
 * fingerprint is required, it is compared server-side, and the copy on this screen says why.
 */

import { useCallback, useState, type JSX, type SyntheticEvent } from 'react';

import { api } from '../shared/api/client';
import {
  Badge,
  Card,
  ErrorBox,
  Formula,
  PageHeader,
  Spinner,
  Stat,
  StatRow,
  Table,
} from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';

export function Contracts(): JSX.Element {
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => {
    setRevision((r) => r + 1);
  }, []);

  const key = useAsync(() => api.contractKey(), [revision]);
  const list = useAsync(() => api.contracts(100), [revision]);
  const me = useAsync(() => api.whoami(), []);
  const [writeError, setWriteError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const roles = me.data?.roles ?? [];
  const canPin = roles.some((r) => ['client_approver', 'platform_admin'].includes(r));
  const canRevoke = roles.some((r) =>
    ['approver', 'tenant_admin', 'client_approver', 'platform_admin'].includes(r),
  );

  const rows = list.data ?? [];
  const live = rows.filter((r) => r.live).length;

  const act = (fn: Promise<unknown>, message: string): void => {
    setWriteError(null);
    setNotice(null);
    void fn
      .then(() => {
        setNotice(message);
        refresh();
      })
      .catch((e: unknown) => {
        setWriteError(e);
      });
  };

  const revoke = (id: string): void => {
    const reason = window.prompt(
      'Why is this contract being revoked? This cannot be undone, and the reason is permanent.',
    );
    if (reason === null || reason.trim() === '') return;
    act(api.revokeContract(id, reason), 'Revoked. The authority it carried has ended.');
  };

  return (
    <>
      <PageHeader
        title="Session contracts"
        subtitle="The document that authorizes a session, signed with a key Conduit never holds. A total compromise of this platform forges none of them."
      />

      <ErrorBox error={key.error} />
      <ErrorBox error={list.error} />
      <ErrorBox error={writeError} />
      {notice !== null && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}

      <Card>
        <StatRow>
          <Stat
            label="Live now"
            value={live}
            tone={live > 0 ? 'info' : 'neutral'}
            hint="unrevoked and inside their window"
          />
          <Stat label="On record" value={rows.length} />
          <Stat
            label="Key pinned"
            value={key.data?.pinned === true ? 'Yes' : 'No'}
            tone={key.data?.pinned === true ? 'ok' : 'danger'}
            hint={
              key.data?.pinned === true
                ? 'contracts can be verified'
                : 'no contract can be accepted until a key is pinned'
            }
          />
        </StatRow>
      </Card>

      <PinKey
        current={key.data?.fingerprint ?? null}
        canPin={canPin}
        onPinned={(f, rotated) => {
          setNotice(
            rotated
              ? `Key rotated. Conduit now trusts ${f}. Every contract signed with the previous key remains verifiable.`
              : `Key pinned. Conduit now trusts ${f}.`,
          );
          refresh();
        }}
        onError={setWriteError}
      />

      <FileContract onFiled={refresh} onError={setWriteError} onNotice={setNotice} />

      <Card
        title="The register"
        subtitle="Every contract ever filed, including the revoked. A contract is the answer to 'under what authority did somebody enter this estate', so none is ever deleted."
      >
        {list.loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['Ticket', 'Window', 'Verified by', 'State', '']}
            empty="No contracts yet. Approve an access request, export its proposal, and have the client sign it."
          >
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <div className="table__strong">{r.ticket_reference}</div>
                  <Formula>{r.access_request_id.slice(0, 8)}</Formula>
                </td>
                <td>
                  <Formula>
                    {r.not_before.replace('T', ' ').slice(0, 16)} →{' '}
                    {r.expires_at.replace('T', ' ').slice(0, 16)}
                  </Formula>
                </td>
                <td>
                  {/* The fingerprint of the key that verified THIS contract, copied at intake.
                      Shown rather than the tenant's current key: after a rotation those differ,
                      and which one signed a given contract is the question that matters. */}
                  <Formula>{r.key_fingerprint}</Formula>
                </td>
                <td>
                  {r.revoked_at !== null ? (
                    <>
                      <Badge tone="muted">revoked</Badge>
                      <Formula>{r.revocation_reason ?? ''}</Formula>
                    </>
                  ) : r.live ? (
                    <Badge tone="info">live</Badge>
                  ) : (
                    <Badge tone="muted">expired</Badge>
                  )}
                </td>
                <td>
                  {r.revoked_at === null && (
                    <button
                      type="button"
                      className="button button--small"
                      disabled={!canRevoke}
                      title={canRevoke ? '' : 'you do not hold revocation authority'}
                      onClick={() => {
                        revoke(r.id);
                      }}
                    >
                      revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

/** The key ceremony. */
function PinKey({
  current,
  canPin,
  onPinned,
  onError,
}: {
  current: string | null;
  canPin: boolean;
  onPinned: (fingerprint: string, rotated: boolean) => void;
  onError: (e: unknown) => void;
}): JSX.Element {
  const [publicKey, setPublicKey] = useState('');
  const [fingerprint, setFingerprint] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // `SyntheticEvent` rather than `FormEvent`: this project's React types mark the Form* aliases
  // deprecated, and the only member used here is `preventDefault`.
  const submit = (e: SyntheticEvent): void => {
    e.preventDefault();
    setBusy(true);
    void api
      .pinContractKey(publicKey.trim(), fingerprint.trim(), reason.trim() === '' ? null : reason)
      .then((r) => {
        setPublicKey('');
        setFingerprint('');
        setReason('');
        onPinned(r.fingerprint, r.rotated);
      })
      .catch(onError)
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Card
      title="Contract key"
      subtitle="The root of trust for every session in this tenant. Whoever controls this key can produce contracts Conduit will honor, and needs nothing else."
    >
      {current !== null && (
        <p>
          Currently trusted: <Formula>{current}</Formula>
          <br />
          <span className="form__hint">
            Ask the client to run <code>conduit-sign fingerprint client.key</code> and confirm it
            matches. This is the check that catches a key substituted in transit, and it is worth
            repeating occasionally rather than only at onboarding.
          </span>
        </p>
      )}

      <form onSubmit={submit} className="form">
        <label className="form__field">
          <span className="form__label">Public key</span>
          <input
            className="form__input"
            value={publicKey}
            onChange={(e) => {
              setPublicKey(e.target.value);
            }}
            placeholder="64 hexadecimal characters, from the client's conduit-sign keygen"
            disabled={!canPin || busy}
            required
          />
        </label>

        <label className="form__field">
          <span className="form__label">Fingerprint, read aloud by the client</span>
          <input
            className="form__input"
            value={fingerprint}
            onChange={(e) => {
              setFingerprint(e.target.value);
            }}
            placeholder="3f:a2:9c:00:41:b7:de:15"
            disabled={!canPin || busy}
            required
          />
          <span className="form__hint">
            <strong>Get this through a different channel from the key itself</strong> — a phone
            call, not the same email. Conduit computes the fingerprint from the key and refuses the
            pin if they disagree. Without this step, pinning is pasting 64 characters somebody sent
            you, and an attacker who intercepted them has replaced the root of trust for this
            client&rsquo;s whole estate.
          </span>
        </label>

        {current !== null && (
          <label className="form__field">
            <span className="form__label">Why is the key being replaced?</span>
            <input
              className="form__input"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder="required when replacing an existing key"
              disabled={!canPin || busy}
            />
            <span className="form__hint">
              A rotation with no reason is indistinguishable from a compromise. Every pin is kept,
              so the client can see in their own ledger that a key changed and why.
            </span>
          </label>
        )}

        <button type="submit" className="button" disabled={!canPin || busy}>
          {busy ? 'Pinning…' : current === null ? 'Pin key' : 'Rotate key'}
        </button>
        {!canPin && (
          <span className="form__hint">
            Pinning is held by the client&rsquo;s own approver, and by a platform administrator so
            that an onboarding can be completed.
          </span>
        )}
      </form>
    </Card>
  );
}

/** Export a proposal, and file the signed result. */
function FileContract({
  onFiled,
  onError,
  onNotice,
}: {
  onFiled: () => void;
  onError: (e: unknown) => void;
  onNotice: (m: string) => void;
}): JSX.Element {
  const [requestId, setRequestId] = useState('');
  const [signed, setSigned] = useState('');
  const [busy, setBusy] = useState(false);

  const exportProposal = (): void => {
    setBusy(true);
    void api
      .contractProposal(requestId.trim())
      .then((proposal) => {
        // Offered as a download rather than shown on screen. The operator's next act is to move
        // this file to a machine Conduit cannot reach, and copy-pasting JSON out of a browser is
        // how a character goes missing and the signature fails for reasons nobody can see.
        const blob = new Blob([JSON.stringify(proposal, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `conduit-proposal-${requestId.trim().slice(0, 8)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        onNotice(
          'Proposal downloaded. Move it to the signing machine and run: conduit-sign sign client.key <proposal> <output>',
        );
      })
      .catch(onError)
      .finally(() => {
        setBusy(false);
      });
  };

  const file = (): void => {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(signed) as Record<string, unknown>;
    } catch {
      onError(new Error('That is not valid JSON. Paste the whole file conduit-sign produced.'));
      return;
    }

    setBusy(true);
    void api
      .submitContract(requestId.trim(), parsed)
      .then((r) => {
        setSigned('');
        onNotice(`Contract filed. It is in force until ${r.expires_at.replace('T', ' ').slice(0, 16)}.`);
        onFiled();
      })
      .catch(onError)
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Card
      title="File a signed contract"
      subtitle="Export the proposal, carry it to the client's signing machine, and bring the signed file back. Conduit has no way to sign one itself, deliberately."
    >
      <div className="form">
        <label className="form__field">
          <span className="form__label">Access request</span>
          <input
            className="form__input"
            value={requestId}
            onChange={(e) => {
              setRequestId(e.target.value);
            }}
            placeholder="the identifier of an approved access request"
            disabled={busy}
          />
          <span className="form__hint">
            Only a request approved by <em>both</em> parties produces a proposal. Asking a client to
            sign for access their own approver has not yet agreed to is how the second approval
            becomes a formality performed afterwards.
          </span>
        </label>

        <button
          type="button"
          className="button"
          onClick={exportProposal}
          disabled={busy || requestId.trim() === ''}
        >
          Export proposal
        </button>

        <label className="form__field">
          <span className="form__label">Signed contract</span>
          <textarea
            className="form__input form__input--area"
            rows={8}
            value={signed}
            onChange={(e) => {
              setSigned(e.target.value);
            }}
            placeholder="paste the JSON that conduit-sign wrote"
            disabled={busy}
          />
          <span className="form__hint">
            Conduit verifies the signature against the pinned key <em>and</em> checks the contract
            against what was approved. A contract may narrow what was approved — that is the client
            exercising judgment — but a contract that widens it is refused, however valid the
            signature.
          </span>
        </label>

        <button
          type="button"
          className="button"
          onClick={file}
          disabled={busy || requestId.trim() === '' || signed.trim() === ''}
        >
          {busy ? 'Filing…' : 'File contract'}
        </button>
      </div>
    </Card>
  );
}
